import { Fragment } from 'react'
import * as Sentry from '@sentry/react'
import { put, call, select, take } from 'redux-saga/effects'
import { show } from 'redux-modal'
import { push, replace } from 'connected-react-router'
import path from 'ramda/es/path'
import jwtDecode from 'jwt-decode'
import { ZendeskAPI } from "react-zendesk/src"

import { identify, logEvent } from 'services/helpers'
import getRedirectPath from 'services/redirectHelper'
import { setToUpperCase } from '../../services/helpers'
import communitySelectors from '../selectors/CommunitySelectors'
import homeSelectors from '../selectors/HomeSelectors'
import profileSelectors from '../selectors/ProfileSelectors'
import userSelectors from '../selectors/UserSelectors'


import {
  ABOUT_ME,
  NO_NETWORK,
  SWIPE,
  WELCOME,
  VERIFY_INFO
} from 'constants/urls'
import API from 'services/api'
import i18n from 'services/i18n'
import { userActions } from '../actions/UserActions'
import { profileActions } from '../actions/ProfileActions'

import { homeActions, homeTypes } from '../actions/HomeActions'
import { communityActions, communityTypes } from '../actions/CommunityActions'
import { swipeActions } from '../actions/SwipeActions'

export function* proceedInvitationLoggedIn(action) {
  const { responseSuccess } = action

  try {
    const invitationData = yield select(homeSelectors.invitationDataSelector)
    const accessToken = yield select(userSelectors.accessTokenSelector)
    const invitationResponse = yield call(API.reassociate, accessToken, invitationData)

    if (invitationData && invitationResponse?.res?.memberships?.length) {
      // Get the membershipId from the invitation data if available
      const selectedMembershipId = invitationData?.membershipId || null

      yield call(proceedSignInSuccess, {
        res: invitationResponse?.res,
        method: 'invitation',
        selectedMembershipId,
        skipMembershipSelect: Boolean(selectedMembershipId)
      })
    } else {
      const description = path(['err', 'error', 'description'], invitationResponse)
      const errorCode = path(['err', 'error', 'code'], invitationResponse)

      if (description) {
        yield put(show(
          'infoModal',
          { label: setToUpperCase(errorCode), description, onHide: !accessToken }
        ))
      }
    }
  } catch (err) {
    const description = path(['error', 'description'], err)
    const errorCode = path(['error', 'code'], err)

    if (description) {
      yield put(show(
        'infoModal',
        { label: setToUpperCase(errorCode), description }
      ))
    }
  }

  yield put(responseSuccess())
}

export function* proceedSignInSuccess(action) {
  const { res, email, name, method, selectedMembershipId, skipMembershipSelect } = action

  try {
    let membership = res.memberships[0]
    let userId = res.user_id
    let membershipId = membership?.membershipId

    const memberships = res.memberships.sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate))

    // Only show membership selection if not skipping and there are multiple memberships
    if (!skipMembershipSelect && memberships.length > 1) {
      yield put(show('membershipSelectModal', { memberships, selectedMembershipId }))

      const { data } = yield take(homeTypes.SET_MEMBERSHIP_ATTEMPT)
      console.log('Received membership data:', data)

      if (data && data.membershipId) {
        membership = data
        membershipId = data.membershipId
      } else {
        console.error('Invalid membership data received')
      }
    } else if (selectedMembershipId) {
      // Use the specified membershipId if provided
      console.log('Using provided membershipId:', selectedMembershipId)
      const selectedMembership = memberships.find(m => m.membershipId === selectedMembershipId)
      if (selectedMembership) {
        membership = selectedMembership
        membershipId = selectedMembershipId
      } else {
        console.error('Selected membership not found:', selectedMembershipId)
      }
    } else if (memberships.length === 0) {
      yield put(
        userActions.updateRegisterData({
          networkId: null,
          subnetworkId: null,
          unitTypeId: null,
          roomTypeId: null
        })
      )
      yield put(replace(NO_NETWORK))
      return
    }

    if (!userId) {
      yield put(
        userActions.updateAuthData({
          membershipId,
          memberships
        })
      )
    }

    if (userId) {
      yield put(
        userActions.updateAuthData({
          accessToken: res.auth_token,
          userId,
          membershipId,
          memberships
        })
      )

      Sentry.configureScope(scope => {
        scope.setUser({ id: userId })
      })
      ZendeskAPI('messenger', 'prefill', {
        name: {
          value: name,
          readOnly: true // optional
        },
        email: {
          value: email,
          readOnly: true // optional
        },
        notes: {
          value: 'test notes info'
        }
      })
      yield call(identify, userId, email)
      yield call(logEvent, `action.SignIn.Method.${method}`)
    }

    yield put(
      userActions.updateRegisterData({
        networkId: membership.l1Id,
        subnetworkId: membership.l2Id,
        unitTypeId: membership.l3Id,
        roomTypeId: membership.l4Id,
        firstName: name && name.split(' ')[0],
        lastName: name && name.split(' ')[1],
        bioEnabled: membership.bioEnabled,
        questionsSaved: membership.questionsSaved,
        email
      })
    )

    yield put(communityActions.getContent())
    yield put(profileActions.getProfile())
    yield take(communityTypes.GET_CONTENT_SUCCESS)

    const initialRoute = yield select(homeSelectors.initialRouteSelector)

    if (initialRoute === '/saved') {
      yield put(homeActions.setInitialRoute(null))
      yield put(push(initialRoute))
    } else if (initialRoute && initialRoute.startsWith('/profile/')) {
      // Handle profile share redirect after authentication
      console.log('Redirecting to profile from ProfileShare:', initialRoute)
      yield put(homeActions.setInitialRoute(null))
      yield put(push(initialRoute))
    } else {
      // Check if matching is finished to determine redirect path
      const isMatchingFinished = yield select(communitySelectors.isMatchingFinishedSelector)
      const matchingRoundStatus = yield select(communitySelectors.matchingRoundStatusSelector)
      const aboutMeHasAllAnswers = yield select(profileSelectors.aboutMeHasAllAnswersSelector)
      const areQuestionsSaved = yield select(userSelectors.areQuestionsSavedSelector)


      const redirectUrl = getRedirectPath(membership, matchingRoundStatus)

      // Check if this is an invitation-based login
      const invitationData = yield select(homeSelectors.invitationDataSelector)
      const isInvitationLogin = Boolean(invitationData?.passcode)

      // If this is an invitation-based login, always redirect to verify info page
      // The verify info page will then redirect to questions page after submission
      if (isInvitationLogin) {
        console.log('Redirecting to Verify Info from invitation')
        yield put(push(VERIFY_INFO))
      } else if ((!aboutMeHasAllAnswers || !areQuestionsSaved) && redirectUrl === SWIPE) {
        yield put(push(ABOUT_ME))
      } else if (isMatchingFinished && redirectUrl === SWIPE) {
        yield put(push(WELCOME))
      } else {
        yield put(push(getRedirectPath(membership, matchingRoundStatus)))
      }




    }
  } catch (err) {
  }
}

export function* proceedSignInFailure(action) {
  const { err } = action || {}

  try {
    if (err) {
      const description = path(['error', 'description'], err)
      const errorCode = path(['error', 'code'], err)

      if (description && errorCode !== 'Unauthorized') {
        yield put(show(
          'infoModal',
          {
            description,
            label: setToUpperCase(errorCode)
          }
        ))
        return
      }
    }

    yield put(show(
      'infoModal',
      {
        showMail: true,
        label: 'Invitation Link is Required',
        description: (
          <Fragment>
            <p>
              You can only register for an account if you have a special invitation link. Depending on your community, this could be sent by them or directly by us at RoomSync.
            </p>
            <p>
              If you've previously accessed your matching group, you may be now logging with a different Facebook, Google, or Apple account that we don't recognize.
            </p>
            For help, please contact us at
            <br />
          </Fragment>
        )
      }
    ))
  } catch (err) {
    console.error('getInvitationPreflight error', err)
  }
}

export function* getInvitationPreflight(action) {
  const { responseSuccess, data: { passcode } } = action

  try {
    const { res, err } = yield call(API.getInvitationPreflight, passcode)
    console.log('getInvitationPreflight res', res)
    const accessToken = yield select(userSelectors.accessTokenSelector)

    if (res && res.invitationType === 'UNIQUE') {
      const method = res?.loginMethod?.charAt(0).toUpperCase() + res?.loginMethod?.slice(1).toLowerCase()

      // Get current user data with proper null checks
      const userId = yield select(userSelectors.userIdSelector)
      const membershipIds = (yield select(userSelectors.membershipsIdsSelector)) ?? []
      const memberships = (yield select(userSelectors.membershipsSelector)) ?? []

      // Check if the invitation's membership is already claimed
      const claimedAndMembershipsMatched = Boolean(method && membershipIds.includes(res.membershipId))

      // If user is logged in but the membership is not in their list, automatically add it
      if (accessToken && userId && !claimedAndMembershipsMatched) {
        yield put(homeActions.setInvitation({
          passcode,
          loginMethod: res.loginMethod,
          membershipId: res.membershipId
        }))
        yield put(homeActions.proceedInvitationLoggedIn())
        yield take(homeTypes.PROCEED_INVITATION_LOGGED_IN_SUCCESS)
        yield put(responseSuccess())
        return
      }

      if (claimedAndMembershipsMatched) {
        // Create a properly structured response object
        const formattedResponse = {
          memberships,
          auth_token: yield select(userSelectors.accessTokenSelector),
          user_id: userId
        }

        yield call(proceedSignInSuccess, {
          res: formattedResponse,
          method: method || 'invitation',
          selectedMembershipId: res.membershipId,
          skipMembershipSelect: true
        })
        yield put(responseSuccess())
        return
      }

      if (method && userId !== res.userId) {
        yield put(homeActions.setInvitation({
          passcode,
          loginMethod: res.loginMethod,
          membershipId: res.membershipId
        }))

        yield put(show(
          'infoModal',
          {
            description: `You have already claimed this invitation. Please sign in with the same ${method} account you used to register. If you have multiple ${method} accounts, please try signing in with each.`,
            label: `Sign in with ${method}`,
          }
        ))
        yield put(responseSuccess())
        return
      }

      // Store the membershipId from the invitation link for auto-selection during registration
      console.log('Setting invitation data with:', {
        passcode,
        loginMethod: res.loginMethod,
        membershipId: res.membershipId
      })
      yield put(homeActions.setInvitation({
        passcode,
        loginMethod: res.loginMethod,
        membershipId: res.membershipId
      }))

      yield put(show('invitationInfoModal', {
        ...res,
        passcode,
        membershipId: res.membershipId
      }))
    } else if (res && res.invitationType === 'GENERAL') {
      yield put(homeActions.setInvitation({ passcode, membershipId: res.membershipId }))

      if (accessToken) {
        yield put(homeActions.proceedInvitationLoggedIn())
        yield take(homeTypes.PROCEED_INVITATION_LOGGED_IN_SUCCESS)
      }
    } else if (err) {
      const description = path(['error', 'description'], err)
      const errorCode = path(['error', 'code'], err)

      yield put(show(
        'infoModal',
        { label: setToUpperCase(errorCode), description }
      ))
    }

    yield put(responseSuccess())
  } catch (err) {
    console.error('getInvitationPreflight error', err)
    yield put(responseSuccess())
    yield put(show(
      'infoModal',
      { label: 'Something went wrong', description: 'Please try again later' }
    ))
  }
}

export function* facebookLogin(action) {
  const { responseSuccess, responseFailure, data: { accessToken, isRegister, name, email } } = action
  // yield call(proceedSignInSuccess, {
  //   res: null,
  //   email: '',
  //   name: '',
  //   method: 'facebook'
  // })
  // return
  try {
    yield call(logEvent, 'action.FacebookLogin')

    if (accessToken) {
      const invitationData = yield select(homeSelectors.invitationDataSelector)
      if (!invitationData && isRegister) {
        yield call(proceedSignInFailure)
        yield put(responseFailure())
        return
      }

      const { res, err } = yield call(API.facebookAuth, accessToken, isRegister, invitationData)

      if (res?.user_id) {
        // Get the membershipId from the invitation data if available
        const selectedMembershipId = invitationData?.membershipId || null

        yield call(proceedSignInSuccess, {
          res,
          email,
          name,
          method: 'facebook',
          selectedMembershipId,
          skipMembershipSelect: Boolean(selectedMembershipId)
        })
        yield put(responseSuccess())
      } else {
        yield call(proceedSignInFailure, { err })
        yield put(responseFailure())
      }
    } else {
      yield put(responseFailure())
    }
  } catch (err) {
    yield put(responseFailure(err))
  }
}

export function* googleLogin(action) {
  const { responseSuccess, responseFailure, data: { code, isRegister, error } } = action

  try {
    if (code) {
      yield call(logEvent, 'action.GoogleLogin')
      const invitationData = yield select(homeSelectors.invitationDataSelector)
      console.log('Google login - invitation data:', invitationData)
      console.log('Google login - isRegister:', isRegister)

      if (!invitationData && isRegister) {
        console.log('Google login - No invitation data and isRegister is true, showing error')
        yield call(proceedSignInFailure)
        yield put(responseFailure())
        return
      }

      console.log('Google login - Calling API.googleAuth with:', { code, isRegister, invitationData })
      const { res, err } = yield call(API.googleAuth, code, isRegister, invitationData)
      console.log('Google login - API response:', { res, err })

      if (res?.user_id) {
        // Get the membershipId from the invitation data if available
        const selectedMembershipId = invitationData?.membershipId || null

        yield call(proceedSignInSuccess, {
          res,
          method: 'google',
          selectedMembershipId,
          skipMembershipSelect: Boolean(selectedMembershipId)
        })
        yield put(responseSuccess())
      } else {
        yield call(proceedSignInFailure, { err })
        yield put(responseFailure())
      }
    } else {
      yield put(responseFailure({ resetInvitation: !error }))
    }
  } catch (err) {
    yield put(responseFailure(err))
  }
}

export function* appleAuth(action) {
  const { responseSuccess, responseFailure, data: { code, identityToken, onError } } = action

  try {
    yield call(logEvent, 'action.AppleLogin')

    const { kid } = jwtDecode(identityToken, { header: true })
    const tokenResponse = jwtDecode(identityToken)

    // Get invitation data from Redux store
    const invitationData = yield select(homeSelectors.invitationDataSelector)

    // Make the API call with invitation data
    const { res, err } = yield call(API.appleAuth, code, true, kid, identityToken, invitationData)

    if (err) {
      yield call(proceedSignInFailure)
      yield put(responseFailure())
      return
    }

    if (res) {
      const isEmailPrivate = (tokenResponse.email || '').includes('@privaterelay.appleid.com')
      const email = isEmailPrivate ? null : tokenResponse.email

      // Get the membershipId from the invitation data if available, otherwise use the first membership
      const selectedMembershipId = invitationData?.membershipId || res.memberships?.[0]?.membershipId || null

      // Call proceedSignInSuccess with the membership information
      yield call(proceedSignInSuccess, {
        res,
        email,
        method: 'apple',
        selectedMembershipId,
        skipMembershipSelect: Boolean(invitationData?.membershipId)
      })
      yield put(responseSuccess())
    } else {
      yield call(proceedSignInFailure, { err })
      yield put(responseFailure())
    }
  } catch (err) {
    onError()
    yield put(responseFailure())
  }
}

export function * getLoginMethod (action) {
  const { responseFailure, responseSuccess, data: { email } } = action

  try {
    const { res } = yield call(API.getLoginMethod, email)

    if (res && res.loginMethods?.length) {
      yield put(show(
        'infoModal',
        {
          label: i18n.t('home.forgotLogin'),
          description: i18n.t('home.methodInfo', { types: res.loginMethods.join(', ') }),
        }
      ))
      yield put(responseSuccess())
    } else {
      yield put(show(
        'infoModal',
        {
          label: i18n.t('home.forgotLogin'),
          description: i18n.t('home.methodInfoNone'),
        }
      ))
      yield put(responseFailure())
    }
  } catch (err) {
    yield put(responseFailure())
  }
}

export function* setMembership(action) {
  const { data } = action

  try {
    // Get the current user data
    const accessToken = yield select(userSelectors.accessTokenSelector)
    const userId = yield select(userSelectors.userIdSelector)
    const memberships = yield select(userSelectors.membershipsSelector)

    if (data) {
      console.log('Switching to membership:', data)

      // Update the auth data with the new membership ID
      yield put(userActions.updateAuthData({
        membershipId: data.membershipId
      }))

      // Reset swipe data to clear old lifestyle preferences and cards
      // This prevents old question IDs from being sent with new questions
      yield put(swipeActions.resetSwipeData())

      // Create a response object similar to what the API would return
      const formattedResponse = {
        memberships,
        auth_token: accessToken,
        user_id: userId
      }

      // Call proceedSignInSuccess to handle the rest of the flow
      yield call(proceedSignInSuccess, {
        res: formattedResponse,
        method: 'switch',
        selectedMembershipId: data.membershipId,
        skipMembershipSelect: true
      })

      // Log the event
      yield call(logEvent, 'action.SwitchMembership')
    } else {
      console.error('No membership data provided')
    }
  } catch (err) {
    console.error('Error switching membership:', err)
  }
}
